import { z } from 'zod'

const name = z.string().trim().min(2, 'Indiquez votre nom (2 caractères minimum).').max(120, '120 caractères maximum.')
const email = z.string().trim().max(160).email('Adresse e-mail invalide.')
const phone = z
  .string()
  .trim()
  .max(30, '30 caractères maximum.')
  .regex(/^[+()\d\s.-]*$/, 'Numéro de téléphone invalide.')
  .optional()
  .or(z.literal(''))
const optText = (max: number) => z.string().trim().max(max, `${max} caractères maximum.`).optional().or(z.literal(''))
const consent = z.literal('on', { message: 'Votre accord est nécessaire pour traiter votre demande.' })

export const PROJECT_TYPES = [
  'Site web ou plateforme',
  'Application mobile',
  'Logiciel métier / développement spécifique',
  'Portail, intranet ou GED',
  'E-commerce',
  'Hébergement / infrastructure / réseaux',
  'Audit ou accompagnement technique',
  'Digitalisation de processus',
  'Autre / je ne sais pas encore',
] as const

export const BUDGETS = [
  'Non défini',
  'Moins de 5 millions FCFA',
  '5 à 15 millions FCFA',
  '15 à 50 millions FCFA',
  'Plus de 50 millions FCFA',
] as const

export const contactSchema = z.object({
  name,
  email,
  phone,
  organisation: optText(160),
  subject: z.string().trim().min(3, 'Précisez l’objet de votre message.').max(160, '160 caractères maximum.'),
  message: z.string().trim().min(20, 'Votre message doit contenir au moins 20 caractères.').max(5000, '5 000 caractères maximum.'),
  consent,
})

export const quoteSchema = z.object({
  name,
  email,
  phone,
  organisation: z.string().trim().min(2, 'Indiquez votre organisation.').max(160, '160 caractères maximum.'),
  role: optText(120),
  projectType: z.enum(PROJECT_TYPES, { message: 'Choisissez un type de projet.' }),
  budget: z.enum(BUDGETS).optional().or(z.literal('')),
  deadline: optText(120),
  message: z.string().trim().min(30, 'Décrivez votre besoin en quelques phrases (30 caractères minimum).').max(8000, '8 000 caractères maximum.'),
  consent,
})

export const applicationSchema = z.object({
  name,
  email,
  phone,
  position: optText(160),
  linkedin: z
    .string()
    .trim()
    .max(300)
    .url('Adresse web invalide (commencez par https://).')
    .refine((v) => /^https?:\/\//i.test(v), 'Adresse web invalide.')
    .optional()
    .or(z.literal('')),
  message: z.string().trim().min(30, 'Présentez-vous en quelques phrases (30 caractères minimum).').max(5000, '5 000 caractères maximum.'),
  jobId: z.string().regex(/^\d*$/).optional().or(z.literal('')),
  consent,
})

export const CV_MAX_BYTES = 5 * 1024 * 1024
export const CV_EXTENSIONS = ['pdf', 'doc', 'docx'] as const

export type FieldErrors = Record<string, string>
export type FormState = { status: 'idle' | 'success' | 'error'; message?: string; fieldErrors?: FieldErrors }

export const toFieldErrors = (issues: z.core.$ZodIssue[]): FieldErrors => {
  const out: FieldErrors = {}
  for (const i of issues) {
    const k = String(i.path[0] ?? 'form')
    if (!out[k]) out[k] = i.message
  }
  return out
}

export const EXPERTISE_TO_PROJECT: Record<string, (typeof PROJECT_TYPES)[number]> = {
  'developpement-web-plateformes': 'Site web ou plateforme',
  'applications-mobiles': 'Application mobile',
  'logiciels-metier': 'Logiciel métier / développement spécifique',
  'portails-intranets': 'Portail, intranet ou GED',
  'e-commerce': 'E-commerce',
  'hebergement-services-informatiques': 'Hébergement / infrastructure / réseaux',
  'audit-informatique': 'Audit ou accompagnement technique',
  'digitalisation-processus': 'Digitalisation de processus',
}
